'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { getExatoHash } from '@/actions';
import { EXATO_DIGITAL_API_KEY } from '@/config';
import { injectExatoDocCheckScript } from '@/utils/exato';

type UseKycValidationParams = {
  customerId: string | undefined;
};

type UseKycValidationReturn = {
  cpf: string | undefined;
  hashCpf: string | undefined;
  isLoadingHash: boolean;
  isExatoReady: boolean;
  restartValidation: () => void;
};

export const useKycValidation = ({
  customerId,
}: UseKycValidationParams): UseKycValidationReturn => {
  const [cpf, setCpf] = useState<string>();
  const [hashCpf, setHashCpf] = useState<string>();
  const [isLoadingHash, setIsLoadingHash] = useState(false);
  const [isExatoReady, setIsExatoReady] = useState(false);
  const [attempt, setAttempt] = useState(0);

  const scriptInjected = useRef(false);
  const hashGenerated = useRef(false);

  const generateHash = useCallback(async () => {
    if (!customerId || hashGenerated.current) return;

    hashGenerated.current = true;
    setIsLoadingHash(true);

    try {
      const response = await getExatoHash({ customerId });

      if (response?.hash && response?.cpf) {
        setHashCpf(response.hash);
        setCpf(response.cpf);
      } else {
        hashGenerated.current = false;
      }
    } catch {
      hashGenerated.current = false;
    } finally {
      setIsLoadingHash(false);
    }
  }, [customerId]);

  useEffect(() => {
    void generateHash();
  }, [attempt, generateHash]);

  const setupExatoDigital = useCallback(() => {
    if (!customerId || !hashCpf) return;

    const win =
      typeof window !== 'undefined'
        ? (window as Window & {
            __exatoDocCheckLoading?: boolean;
            __exatoDocCheckInitPromise?: Promise<void>;
          })
        : null;

    const hasExistingIframe =
      typeof document !== 'undefined' &&
      !!document.querySelector("iframe[src*='exato'],iframe[src*='doccheck']");

    if (
      scriptInjected.current &&
      !hasExistingIframe &&
      !win?.__exatoDocCheckLoading &&
      !win?.__exatoDocCheckInitPromise
    ) {
      scriptInjected.current = false;
    }

    if (
      scriptInjected.current ||
      hasExistingIframe ||
      win?.__exatoDocCheckLoading ||
      win?.__exatoDocCheckInitPromise
    ) {
      if (hasExistingIframe) {
        setIsExatoReady(true);
      }
      return;
    }

    scriptInjected.current = true;

    injectExatoDocCheckScript(EXATO_DIGITAL_API_KEY, () => {
      setIsExatoReady(true);
    });
  }, [customerId, hashCpf]);

  useEffect(() => {
    setupExatoDigital();
  }, [setupExatoDigital]);

  const restartValidation = useCallback(() => {
    hashGenerated.current = false;
    scriptInjected.current = false;
    setCpf(undefined);
    setHashCpf(undefined);
    setIsExatoReady(false);
    setAttempt((current) => current + 1);
  }, []);

  return {
    cpf,
    hashCpf,
    isLoadingHash,
    isExatoReady,
    restartValidation,
  };
};
